// src/services/adminService.ts
import unifiedAuth from '../utils/unifiedAuth';
import adminAuth from '../utils/adminAuth';
import { config } from '../config';
import adminLiveVideoService from './adminLiveVideoService';

interface BulkActionResult<T = unknown> {
  success: boolean;
  item?: T;
  userId?: string;
  result?: unknown;
  error?: unknown;
}

export interface AdminActivity {
  id: string;
  admin: {
    username: string;
  };
  activity_type: string;
  description: string;
  timestamp: string;
}

const API_BASE_URL = config.apiBaseUrl;

/**
 * The server's own explanation for a failed request, or null when there is not one.
 *
 * Reading the body can itself fail - an HTML error page from a proxy, or a body already
 * consumed - and a thrown parser is not worth turning into the error the admin sees, so a
 * failure here just falls back to the status code.
 */
async function serverMessage(response: Response): Promise<string | null> {
  try {
    const body = await response.json();
    const message = body?.message ?? body?.error;
    return typeof message === 'string' && message.trim() ? message : null;
  } catch {
    return null;
  }
}

export interface DashboardStats {
  // User Statistics
  total_users: number;
  new_users_today: number;
  active_users_today: number;
  verified_users: number;
  pending_users: number;
  admin_users: number;
  
  // Content Statistics
  total_originals: number;
  total_cinema: number;
  total_content: number;
  total_podcasts: number;
  total_animations: number;
  total_sneak_peeks: number;
  total_live_videos: number;
  total_all_content: number;
  
  // Engagement Statistics
  total_comments: number;
  total_likes: number;
  total_views: number;
  
  // Trending Content
  trending_originals: Array<{
    id: string;
    title: string;
    author__username: string;
    read_count: number;
    like_count: number;
  }>;
  
  trending_cinema: Array<{
    id: string;
    title: string;
    author__username: string;
    view_count: number;
    like_count: number;
  }>;
  
  trending_podcasts: Array<{
    id: string;
    title: string;
    author__username: string;
    play_count: number;
    like_count: number;
  }>;
  
  trending_animations: Array<{
    id: string;
    title: string;
    author__username: string;
    view_count: number;
    like_count: number;
  }>;
  
  // Most Active Users
  most_active_users: Array<{
    id: string;
    username: string;
    first_name: string;
    last_name: string;
    content_count: number;
  }>;
  
  // Recent Activities
  recent_activities: Array<{
    id: string;
    admin: {
      username: string;
    };
    activity_type: string;
    description: string;
    timestamp: string;
  }>;
  
  // Live Video Statistics
  live_video_stats: {
    currently_live: number;
    upcoming_videos: number;
    past_videos: number;
    total_live_views: number;
    current_viewers: number;
    avg_views_per_live_video: number;
    top_performing_live_video?: {
      id: string;
      title: string;
      viewCount: number;
      currentViewers?: number;
    };
  };
  
  // Additional Metrics
  pending_moderation: number;
  avg_session_duration: number;
  /** Null when there is no session tracking behind it. Was the constant 23.4. */
  bounce_rate: number | null;
  email_stats: {
    total_subscribers: number;
    verified_subscribers: number;
    emails_sent_today: number;
    failed_emails_today: number;
  };
}

export interface ContentItem {
  id: string;
  title: string;
  description?: string;
  contentType: 'CINEMA' | 'CONTENT' | 'PODCAST' | 'ANIMATION' | 'SNEAK_PEEK' | 'LIVE_VIDEO';
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED' | 'SCHEDULED';
  /** BBFC rating. Absent means unrated, which AgeGate treats as adults-only. */
  ageRating?: 'U' | 'PG' | '12' | '15' | '18';
  /** Set when this item streams from Bunny Stream; absent means the original file. */
  bunnyVideoId?: string | null;
  category?: string;
  tags?: string;
  thumbnailUrl?: string;
  author: {
    id: string;
    username: string;
    firstName?: string;
    lastName?: string;
  };
  viewCount: number;
  likeCount: number;
  isFeatured: boolean;
  isPremium: boolean;
  createdAt: string;
  updatedAt?: string;
  publishedAt?: string;
}

/**
 * A job somebody can hold on the team.
 *
 * `name` is the stable key stored against the user and is never renamed; `label` is what a
 * person reads and changes freely. `active` false means retired: out of the dropdown, but
 * still showing as the title of anybody who already holds it.
 */
export interface TeamPosition {
  id: string;
  name: string;
  label: string;
  displayOrder: number;
  active: boolean;
  /**
   * Optimistic lock, sent back when reordering to prove the screen is still current.
   *
   * Optional because the field arrived after the endpoint did: a client talking to a
   * backend that predates it gets undefined, and the server treats a missing version as a
   * conflict rather than as permission to skip the check.
   */
  version?: number;
}

export interface User extends Record<string, unknown> {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  isStaff: boolean;
  isAdminUser: boolean;
  /** Their job on the team, when they hold a staff or admin role. */
  teamPosition?: string | null;
  teamPositionLabel?: string | null;
  dateJoined: string;
  lastLogin: string | null;
  isVerified: boolean;
  phoneNumber?: string;
  avatarUrl?: string;
  country?: string;
}

// Helper function to convert MySQL BIT(1) or various boolean representations to boolean
function convertBitToBoolean(value: unknown): boolean {
  if (value === null || value === undefined) {
    return false;
  }
  
  // Handle boolean values
  if (typeof value === 'boolean') {
    return value;
  }
  
  // Handle number values (0 = false, 1 = true)
  if (typeof value === 'number') {
    return value === 1;
  }
  
  // Handle string values
  if (typeof value === 'string') {
    const str = value.toLowerCase().trim();
    if (str === 'true' || str === '1') return true;
    if (str === 'false' || str === '0' || str === '') return false;
  }
  
  // Handle binary data that might come from MySQL BIT fields
  if (value && typeof value === 'object') {
    // Check if it's an array-like object with binary data
    if ('length' in value && typeof (value as Record<string, unknown>).length === 'number') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const arr = value as any;
      if (arr.length > 0) {
        return arr[0] === 1 || arr[0] === true;
      }
    }
    
    // Try to convert object to string
    try {
      const str = String(value).toLowerCase();
      return str === 'true' || str === '1';
    } catch {
      return false;
    }
  }
  
  return false;
}

// Helper function to normalize user data from backend
function normalizeUserData(user: Record<string, unknown>): User {
  // The username arrives as a username. It used to be split on "@" because the backend
  // serialised the User entity, whose getUsername() returns the email address - the
  // UserDetails contract trap. AdminUserListItem sends getDisplayUsername(), so there is
  // nothing to strip, and stripping would corrupt a username that legitimately had one.
  const actualUsername = user.username as string;

  // Real booleans now. These went through a BIT-field converter that read a missing
  // value as false, and the fields were missing - @JsonIgnore on the entity stripped
  // isStaff, isAdminUser and isVerified from this endpoint. Every account therefore
  // showed as not-admin and not-verified, and somebody had patched around it by
  // hardcoding one username:
  //
  //   if (!isVerified && actualUsername === '<one real username>') { isVerified = true; }
  //   if (!isAdminUser && actualUsername === '<the same one>') { isAdminUser = true; }
  //
  // That made one row look right and left every other row wrong. Gone, with the
  // guesswork, now that the values arrive as booleans.
  const isVerified = convertBitToBoolean(user.isVerified);
  const isAdminUser = convertBitToBoolean(user.isAdminUser);
  const isStaff = convertBitToBoolean(user.isStaff);
  const isActive = convertBitToBoolean(user.isActive);

  const normalized = {
    id: user.id as string,
    username: actualUsername,
    email: user.email as string,
    firstName: user.firstName as string,
    lastName: user.lastName as string,
    phoneNumber: user.phoneNumber as string,
    country: user.country as string,
    avatarUrl: user.avatarUrl as string,
    teamPosition: (user.teamPosition as string) ?? null,
    teamPositionLabel: (user.teamPositionLabel as string) ?? null,
    isVerified,
    isAdminUser,
    isActive,
    isStaff,
    dateJoined: user.dateJoined as string,
    lastLogin: user.lastLogin as string | null,
  };
  
  return normalized;
}

export interface AdminActivity {
  id: string;
  admin: {
    username: string;
  };
  activity_type: string;
  description: string;
  timestamp: string;
  content_type?: string;
  object_id?: string;
}

export interface ModerationItem {
  id: string;
  content_type: string;
  object_id: string;
  submitted_by: {
    username: string;
  };
  status: string;
  priority: number;
  reason: string;
  submitted_at: string;
}

class AdminService {
  /**
   * Check if the current user has admin access
   */
  hasAdminAccess(): boolean {
    const isAuthenticated = unifiedAuth.isAuthenticated();
    const isAdmin = adminAuth.isAdminAuthenticated();
    return isAuthenticated && isAdmin;
  }

  /**
   * Ensure admin authentication before making requests
   */
  private ensureAdminAuth(): void {
    // Check if user is authenticated first
    if (!unifiedAuth.isAuthenticated()) {
      throw new Error('Authentication required. Please log in.');
    }
    
    // Check admin privileges - be more lenient to avoid infinite loops
    if (!adminAuth.isAdminAuthenticated()) {
      throw new Error('Admin privileges required. Contact administrator for access.');
    }
  }

  private getAuthHeaders() {
    // No longer throws when the access token is missing. It used to, which meant a
    // user whose token had merely expired got "Please login" even though a valid
    // refresh token was sitting right there. unifiedAuth.fetch now refreshes and
    // the request succeeds; a genuinely dead session still ends in a 401.
    return {
      'Content-Type': 'application/json',
    };
  }

  private async makeRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    // Build URL more carefully to avoid double /api
    let baseUrl = API_BASE_URL || '';
    
    // Remove trailing slashes
    baseUrl = baseUrl.replace(/\/+$/, '');
    
    // If baseUrl already contains /api, don't add it again
    let url: string;
    if (baseUrl.endsWith('/api')) {
      url = `${baseUrl}/admin/${endpoint}`;
    } else {
      url = `${baseUrl}/api/admin/${endpoint}`;
    }
    
    // Ensure admin authentication before making any request
    this.ensureAdminAuth();
    
    const headers = this.getAuthHeaders();

    // Token attachment + 401 refresh-retry is delegated to unifiedAuth.fetch()
    const response = await unifiedAuth.fetch(url, {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error(`Admin access denied: ${response.status}. You may need to refresh your login to access admin features.`);
      }
      if (response.status === 404) {
        throw new Error(`Endpoint not found: ${response.status}. Analytics endpoint may not be implemented yet.`);
      }
      // The server explains its refusals - "3 people hold this position, retire it
      // instead" - and this used to throw the bare status code and drop the body, so the
      // admin saw "HTTP error! status: 400" and had to guess. Every other service in this
      // repo already reads the message first.
      throw new Error(await serverMessage(response) || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    // Extract data from ApiResponse wrapper if it exists
    if (data.data) {
      return data.data;
    }
    
    return data;
  }



  // Dashboard Statistics
  async getDashboardStats(): Promise<DashboardStats> {
    try {
      // Get base stats from backend
      const baseStats = await this.makeRequest<DashboardStats>('stats/');
      
      // Get enhanced live video stats
      const liveVideoStats = await adminLiveVideoService.getLiveVideoStats();
      
      // Merge the statistics
      return {
        ...baseStats,
        live_video_stats: {
          currently_live: liveVideoStats.currentlyLive,
          upcoming_videos: liveVideoStats.upcomingVideos,
          past_videos: liveVideoStats.pastVideos,
          total_live_views: liveVideoStats.totalViews,
          current_viewers: liveVideoStats.totalCurrentViewers,
          avg_views_per_live_video: liveVideoStats.avgViewsPerVideo,
          top_performing_live_video: liveVideoStats.topPerformingVideo
        }
      };
    } catch (error) {
      console.error('Failed to fetch enhanced dashboard stats:', error);
      // Fallback to base stats with empty live video stats
      const baseStats = await this.makeRequest<DashboardStats>('stats/');
      return {
        ...baseStats,
        live_video_stats: {
          currently_live: 0,
          upcoming_videos: 0,
          past_videos: 0,
          total_live_views: 0,
          current_viewers: 0,
          avg_views_per_live_video: 0
        }
      };
    }
  }

  // Analytics endpoints
  async getAnalyticsDashboard(dateRange: string = '7d'): Promise<{
    overview: {
      total_views: number;
      total_users: number;
      total_content: number;
      engagement_rate: number;
      avg_session_duration: number;
      bounce_rate: number | null;
    };
    content_performance: Array<{
      id: string;
      title: string;
      content_type: string;
      views: number;
      likes: number;
      engagement_rate: number;
      trending: boolean;
    }>;
    user_engagement: Array<{
      date: string;
      daily_active_users: number;
      new_users: number;
      returning_users: number;
    }>;
    content_distribution: Array<{
      content_type: string;
      count: number;
      percentage: number;
    }>;
    geographic_data: Array<{
      country: string;
      user_count: number;
      percentage: number;
    }>;
  }> {
    const params = new URLSearchParams({ dateRange });
    return await this.makeRequest(`analytics/dashboard?${params.toString()}`);
  }

  async getOverviewAnalytics(dateRange: string = '7d'): Promise<{
    total_views: number;
    total_users: number;
    total_content: number;
    engagement_rate: number;
    avg_session_duration: number;
    bounce_rate: number | null;
  }> {
    const params = new URLSearchParams({ dateRange });
    return await this.makeRequest(`analytics/overview?${params.toString()}`);
  }

  async getContentPerformance(limit: number = 10): Promise<Array<{
    id: string;
    title: string;
    content_type: string;
    views: number;
    likes: number;
    engagement_rate: number;
    trending: boolean;
  }>> {
    const params = new URLSearchParams({ limit: limit.toString() });
    return await this.makeRequest(`analytics/content-performance?${params.toString()}`);
  }

  async getUserEngagementTrend(dateRange: string = '7d'): Promise<Array<{
    date: string;
    daily_active_users: number;
    new_users: number;
    returning_users: number;
  }>> {
    const params = new URLSearchParams({ dateRange });
    return await this.makeRequest(`analytics/user-engagement?${params.toString()}`);
  }

  async getContentDistribution(): Promise<Array<{
    content_type: string;
    count: number;
    percentage: number;
  }>> {
    return await this.makeRequest('analytics/content-distribution');
  }

  async getGeographicData(): Promise<Array<{
    country: string;
    user_count: number;
    percentage: number;
  }>> {
    return await this.makeRequest('analytics/geographic');
  }

  // Content Management
  async getContentManagement(filters: {
    content_type?: string;
    status?: string;
    search?: string;
    page?: number;
    page_size?: number;
  } = {}): Promise<{
    content: ContentItem[];
    total_count: number;
    page?: number;
    page_size?: number;
    total_pages?: number;
  }> {
    const params = new URLSearchParams();
    if (filters.content_type) params.append('content_type', filters.content_type);
    if (filters.status) params.append('status', filters.status);
    if (filters.search) params.append('search', filters.search);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.page_size) params.append('page_size', filters.page_size.toString());
    
    const endpoint = `content/${params.toString() ? `?${params.toString()}` : ''}`;
    return await this.makeRequest<{
      content: ContentItem[];
      total_count: number;
      page?: number;
      page_size?: number;
      total_pages?: number;
    }>(endpoint);
  }

  // User Management
  async getUserManagement(filters: {
    status?: string;
    search?: string;
    page?: number;
    page_size?: number;
  } = {}): Promise<{
    users: User[];
    total_count: number;
    page?: number;
    page_size?: number;
    total_pages?: number;
  }> {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.search) params.append('search', filters.search);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.page_size) params.append('page_size', filters.page_size.toString());
    
    const endpoint = `users/${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await this.makeRequest<{
      users: Record<string, unknown>[];
      total_count: number;
      page?: number;
      page_size?: number;
      total_pages?: number;
    }>(endpoint);
    
    // Normalize user data to handle BIT fields properly
    return {
      ...response,
      users: response.users.map(user => normalizeUserData(user))
    };
  }

  // Admin Activities
  async getAdminActivities(page = 1, pageSize = 50): Promise<{
    activities: AdminActivity[];
    total_count: number;
    page: number;
    page_size: number;
    has_next: boolean;
  }> {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });
    
    return this.makeRequest<{
      activities: AdminActivity[];
      total_count: number;
      page: number;
      page_size: number;
      has_next: boolean;
    }>(`activities/?${params.toString()}`);
  }

  // Moderation Queue
  async getModerationQueue(status = 'pending'): Promise<{
    queue_items: ModerationItem[];
    total_count: number;
  }> {
    const params = new URLSearchParams({
      status,
    });
    
    return this.makeRequest<{
      queue_items: ModerationItem[];
      total_count: number;
    }>(`moderation/?${params.toString()}`);
  }

  // User Actions
  async getUserById(userId: string): Promise<User> {
    const response = await this.makeRequest<{ user: User }>(`users/${userId}/`, {
      method: 'GET',
    });
    return normalizeUserData(response.user);
  }

  async makeUserAdmin(userId: string): Promise<{ message: string; user: User }> {
    return this.makeRequest<{ message: string; user: User }>(`users/${userId}/make-admin/`, {
      method: 'POST',
    });
  }

  /**
   * Invalidates a user's password, revokes every session, and emails them a reset code.
   *
   * Three things in one call deliberately. Done by hand, two get missed: the password
   * column is NOT NULL so it cannot be emptied, and a direct database edit does not
   * revoke tokens - so a 7-day refresh token keeps whoever holds a live session signed
   * in for a week after the password is scrambled, which is the person being locked out.
   */
  async forcePasswordReset(userId: string): Promise<{
    userId: string;
    passwordInvalidated: boolean;
    sessionsRevoked: boolean;
    resetEmailSent: boolean;
  }> {
    return this.makeRequest(`users/${userId}/force-password-reset/`, {
      method: 'POST',
    });
  }

  /**
   * Gives somebody a staff role and a position on the team.
   *
   * Independent of admin since the backend decoupled them: makeUserAdmin used to set both
   * flags, so staff and admin were the same thing and nobody could be one without the
   * other.
   */
  async makeUserStaff(userId: string, position: string): Promise<{
    userId: string; isStaff: boolean; teamPosition: string;
  }> {
    return this.makeRequest(
      `users/${userId}/make-staff/?position=${encodeURIComponent(position)}`,
      { method: 'POST' },
    );
  }

  async removeUserStaff(userId: string): Promise<{ userId: string; isStaff: boolean }> {
    return this.makeRequest(`users/${userId}/remove-staff/`, { method: 'POST' });
  }

  // ---------------------------------------------------------------------------
  // Team positions
  //
  // Rows rather than an enum, so a position added today reaches the dropdown without a
  // deploy. There used to be a hand-written copy of the list in the frontend; a second
  // copy is a second thing to forget.
  // ---------------------------------------------------------------------------

  /** Positions available to assign: active only, in the order an admin arranged them. */
  async getTeamPositions(): Promise<TeamPosition[]> {
    return this.makeRequest<TeamPosition[]>('team-positions');
  }

  /** Every position, retired ones included, for the screen that manages them. */
  async getAllTeamPositions(): Promise<TeamPosition[]> {
    return this.makeRequest<TeamPosition[]>('team-positions/all');
  }

  /**
   * Adds a position. Only the label is sent: the key is derived from it server-side, so
   * two admins typing "Sound Recordist" cannot create two positions.
   */
  async createTeamPosition(label: string, displayOrder?: number): Promise<TeamPosition> {
    return this.makeRequest<TeamPosition>('team-positions', {
      method: 'POST',
      body: JSON.stringify({ label, displayOrder }),
    });
  }

  /**
   * Renames, reorders, retires or reinstates a position. Omitted fields are left alone.
   * The key is never changed - it is what the user row holds, so renaming it would
   * unassign every holder.
   */
  async updateTeamPosition(
    id: string,
    changes: { label?: string; displayOrder?: number; active?: boolean },
  ): Promise<TeamPosition> {
    return this.makeRequest<TeamPosition>(`team-positions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(changes),
    });
  }

  /**
   * Puts the positions in the given order, renumbering them 10, 20, 30 ...
   *
   * One request rather than swapping two neighbours' numbers. Swapping assumed the numbers
   * were distinct and nothing guaranteed that: positions created without an explicit order
   * all landed on 999, and the arrows that swapped them did nothing because each write set
   * the value it already held.
   *
   * Takes whole positions rather than ids because each one's version goes with it. A 409
   * means somebody else changed the list while this screen was open; the message explains
   * it and the caller should reload.
   */
  async reorderTeamPositions(positions: TeamPosition[]): Promise<TeamPosition[]> {
    return this.makeRequest<TeamPosition[]>('team-positions/order', {
      method: 'PUT',
      // Each position's version as this screen loaded it. The server refuses the whole
      // reorder if any of them has moved since, which is what stops two admins
      // reordering at once from silently discarding each other's arrangement.
      body: JSON.stringify({
        positions: positions.map((p) => ({ id: p.id, version: p.version })),
      }),
    });
  }

  /**
   * Deletes a position. Refused while somebody holds it, and the refusal says how many
   * and suggests retiring instead - which is why makeRequest now passes the server's
   * message through rather than throwing the status code.
   */
  async deleteTeamPosition(id: string): Promise<{ name: string }> {
    return this.makeRequest<{ name: string }>(`team-positions/${id}`, { method: 'DELETE' });
  }

  async removeUserAdmin(userId: string): Promise<{ message: string; user: User }> {
    return this.makeRequest<{ message: string; user: User }>(`users/${userId}/remove-admin/`, {
      method: 'POST',
    });
  }

  async updateUser(userId: string, userData: {
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    is_admin: boolean;
  }): Promise<{ message: string; user: User }> {
    return this.makeRequest<{ message: string; user: User }>(`users/${userId}/update/`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }

  async deleteUser(userId: string): Promise<{ message: string }> {
    return this.makeRequest<{ message: string }>(`users/${userId}/delete/`, {
      method: 'DELETE',
    });
  }

  async createUser(userData: {
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    password: string;
  }): Promise<{ message: string; user: User }> {
    return this.makeRequest<{ message: string; user: User }>('users/create/', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  // Content Actions
  async deleteContent(contentType: string, contentId: string): Promise<{ message: string }> {
    return this.makeRequest<{ message: string }>(`content/${contentType}/${contentId}/delete/`, {
      method: 'DELETE',
    });
  }

  async createContent(formData: FormData): Promise<{ message: string; content: ContentItem }> {
    // Build URL more carefully to avoid double /api
    let baseUrl = API_BASE_URL || '';
    
    // Remove trailing slashes
    baseUrl = baseUrl.replace(/\/+$/, '');
    
    // If baseUrl already contains /api, don't add it again
    let url: string;
    if (baseUrl.endsWith('/api')) {
      url = `${baseUrl}/admin/content/create`;
    } else {
      url = `${baseUrl}/api/admin/content/create`;
    }
    
    // Ensure admin authentication before making any request
    this.ensureAdminAuth();
    
    const token = unifiedAuth.getAccessToken();
    if (!token) {
      throw new Error('No authentication token found. Please login.');
    }

    // Token attachment + 401 refresh-retry is delegated to unifiedAuth.fetch()
    const response = await unifiedAuth.fetch(url, {
      method: 'POST',
      // Don't set Content-Type for FormData - let browser set it with boundary
      body: formData,
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error(`Admin access denied: ${response.status}. You may need to refresh your login to access admin features.`);
      }
      if (response.status === 404) {
        throw new Error(`Endpoint not found: ${response.status}. Content creation endpoint may not be implemented yet.`);
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    // Extract data from ApiResponse wrapper if it exists
    if (data.data) {
      return data.data;
    }
    
    return data;
  }

  async updateContent(contentId: string, formData: FormData): Promise<{ message: string; content: ContentItem }> {
    // Build URL more carefully to avoid double /api
    let baseUrl = API_BASE_URL || '';
    
    // Remove trailing slashes
    baseUrl = baseUrl.replace(/\/+$/, '');
    
    // If baseUrl already contains /api, don't add it again
    let url: string;
    if (baseUrl.endsWith('/api')) {
      url = `${baseUrl}/admin/content/${contentId}/update`;
    } else {
      url = `${baseUrl}/api/admin/content/${contentId}/update`;
    }
    
    // Ensure admin authentication before making any request
    this.ensureAdminAuth();
    
    const token = unifiedAuth.getAccessToken();
    if (!token) {
      throw new Error('No authentication token found. Please login.');
    }

    // Token attachment + 401 refresh-retry is delegated to unifiedAuth.fetch()
    const response = await unifiedAuth.fetch(url, {
      method: 'PUT',
      // Don't set Content-Type for FormData - let browser set it with boundary
      body: formData,
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error(`Admin access denied: ${response.status}. You may need to refresh your login to access admin features.`);
      }
      if (response.status === 404) {
        throw new Error(`Endpoint not found: ${response.status}. Content update endpoint may not be implemented yet.`);
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    // Extract data from ApiResponse wrapper if it exists
    if (data.data) {
      return data.data;
    }
    
    return data;
  }

  // Email Notifications
  async getEmailNotificationStats(): Promise<{
    total_subscribers: number;
    verified_subscribers: number;
    emails_sent_today: number;
    failed_emails_today: number;
    recent_notifications: Array<{
      id: string;
      notification_type: string;
      status: string;
      sent_at: string;
      recipient_count: number;
    }>;
  }> {
    return this.makeRequest<{
      total_subscribers: number;
      verified_subscribers: number;
      emails_sent_today: number;
      failed_emails_today: number;
      recent_notifications: Array<{
        id: string;
        notification_type: string;
        status: string;
        sent_at: string;
        recipient_count: number;
      }>;
    }>('notifications/stats/');
  }

  // Live Stream Management
  async startLiveStream(data: {
    title: string;
    description: string;
    scheduled_start_time: string;
  }): Promise<{ message: string; live_video_id: string }> {
    return this.makeRequest<{ message: string; live_video_id: string }>('live/start/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Bulk Actions
  async bulkDeleteContent(contentItems: { content_type: string; content_id: string }[]): Promise<{ message: string; results: BulkActionResult[] }> {
    const results: BulkActionResult[] = [];
    for (const item of contentItems) {
      try {
        const result = await this.deleteContent(item.content_type, item.content_id);
        results.push({ success: true, item, result });
      } catch (_error) {
        results.push({ success: false, item, error: _error });
      }
    }
    return { 
      message: `Bulk delete completed. ${results.filter(r => r.success).length} successful, ${results.filter(r => !r.success).length} failed.`,
      results 
    };
  }

  async bulkMakeUsersAdmin(userIds: string[]): Promise<{ message: string; results: BulkActionResult[] }> {
    const results: BulkActionResult[] = [];
    for (const userId of userIds) {
      try {
        const result = await this.makeUserAdmin(userId);
        results.push({ success: true, userId, result });
      } catch (_error) {
        results.push({ success: false, userId, error: _error });
      }
    }
    return { 
      message: `Bulk admin assignment completed. ${results.filter(r => r.success).length} successful, ${results.filter(r => !r.success).length} failed.`,
      results 
    };
  }

  async notifyContentUsers(contentId: string): Promise<{ message: string; content_id: string }> {
    let baseUrl = API_BASE_URL || '';
    baseUrl = baseUrl.replace(/\/+$/, '');

    const url = baseUrl.endsWith('/api')
      ? `${baseUrl}/email-notifications/notify-content/${contentId}`
      : `${baseUrl}/api/email-notifications/notify-content/${contentId}`;

    this.ensureAdminAuth();
    const headers = this.getAuthHeaders();

    // Token attachment + 401 refresh-retry is delegated to unifiedAuth.fetch()
    const response = await unifiedAuth.fetch(url, {
      method: 'POST',
      headers,
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Content not found');
      }
      throw new Error(`Failed to send notifications: ${response.status}`);
    }

    const data = await response.json();
    return data.data || data;
  }

  async bulkDeleteUsers(userIds: string[]): Promise<{ message: string; results: BulkActionResult[] }> {
    const results: BulkActionResult[] = [];
    for (const userId of userIds) {
      try {
        const result = await this.deleteUser(userId);
        results.push({ success: true, userId, result });
      } catch (_error) {
        results.push({ success: false, userId, error: _error });
      }
    }
    return { 
      message: `Bulk user deletion completed. ${results.filter(r => r.success).length} successful, ${results.filter(r => !r.success).length} failed.`,
      results 
    };
  }
}

export default new AdminService();
